import { useMemo, useState } from 'react';
import type { HazmatInfo } from '../../../types/label';
import {
  AddressField,
  AddressType,
  CustomsInformation,
  CustomsItemDraft,
  DEFAULT_HAZMAT_INFO,
  DestinationAddress,
  ItemDraft,
  LabelFormat,
  LabelFormState,
  LIMITED_QUANTITY,
  PackageDraft,
  allItems,
  copyPackage,
  createInitialState,
  newCustomsItem,
  newItem,
  newPackage,
  nextPackageRef,
} from '../labelForm';

type Keyed = { key: string };

const patchByKey = <T extends Keyed>(rows: T[], key: string, patch: (row: T) => Partial<T>): T[] =>
  rows.map((row) => (row.key === key ? { ...row, ...patch(row) } : row));

const withoutKey = <T extends Keyed>(rows: T[], key: string): T[] => rows.filter((row) => row.key !== key);

/** Owns the label form draft. All callbacks are referentially stable. */
export function useLabelForm() {
  const [form, setForm] = useState<LabelFormState>(createInitialState);

  const actions = useMemo(() => {
    const update = (patch: (prev: LabelFormState) => Partial<LabelFormState>) =>
      setForm((prev) => ({ ...prev, ...patch(prev) }));
    // Only the package holding the item gets a new object, so other memoized package cards skip re-render.
    const updateItemRow = (key: string, patch: (row: ItemDraft) => Partial<ItemDraft>) =>
      update((prev) => ({
        packages: prev.packages.map((pkg) =>
          pkg.items.some((item) => item.key === key) ? { ...pkg, items: patchByKey(pkg.items, key, patch) } : pkg,
        ),
      }));

    return {
      setTestMode: (testMode: boolean) => update(() => ({ testMode })),
      setGenerateLabels: (generateLabels: boolean) => update(() => ({ generateLabels })),
      toggleFormat: (format: LabelFormat) =>
        update((prev) => ({
          labelFormats: prev.labelFormats.includes(format)
            ? prev.labelFormats.filter((f) => f !== format)
            : [...prev.labelFormats, format],
        })),

      updatePackage: (key: string, patch: Partial<PackageDraft>) =>
        update((prev) => ({ packages: patchByKey(prev.packages, key, () => patch) })),
      addPackage: () =>
        update((prev) => ({
          packages: [...prev.packages, newPackage(nextPackageRef(prev.packages), prev.packages[prev.packages.length - 1])],
        })),
      duplicatePackage: (key: string) =>
        update((prev) => {
          const index = prev.packages.findIndex((p) => p.key === key);
          if (index < 0) return {};
          const packages = prev.packages.slice();
          packages.splice(index + 1, 0, copyPackage(prev.packages[index], nextPackageRef(prev.packages)));
          return { packages };
        }),
      removePackage: (key: string) =>
        update((prev) => (prev.packages.length > 1 ? { packages: withoutKey(prev.packages, key) } : {})),

      updateItem: (key: string, patch: Partial<ItemDraft>) => updateItemRow(key, () => patch),
      toggleLimitedQuantity: (key: string) =>
        updateItemRow(key, (row) => {
          const details = row.productDetails || [];
          return {
            productDetails: details.includes(LIMITED_QUANTITY)
              ? details.filter((d) => d !== LIMITED_QUANTITY)
              : [...details, LIMITED_QUANTITY],
          };
        }),
      // Unchecking keeps hazmatInfo so re-enabling restores it (pre-redesign behavior).
      toggleHazmat: (key: string) =>
        updateItemRow(key, (row) => ({ hazmat: !row.hazmat, hazmatInfo: row.hazmatInfo ?? { ...DEFAULT_HAZMAT_INFO } })),
      updateHazmat: (key: string, patch: Partial<HazmatInfo>) =>
        updateItemRow(key, (row) => ({ hazmatInfo: { ...(row.hazmatInfo ?? DEFAULT_HAZMAT_INFO), ...patch } })),
      addItem: (packageKey: string) =>
        update((prev) => {
          const productId = `PRODUCT${allItems(prev.packages).length + 1}`;
          return { packages: patchByKey(prev.packages, packageKey, (pkg) => ({ items: [...pkg.items, newItem(productId)] })) };
        }),
      // Every package keeps at least one item.
      removeItem: (key: string) =>
        update((prev) => ({
          packages: prev.packages.map((pkg) =>
            pkg.items.length > 1 && pkg.items.some((item) => item.key === key) ? { ...pkg, items: withoutKey(pkg.items, key) } : pkg,
          ),
        })),

      setAddressField: (field: AddressField, value: string) =>
        update((prev) => ({
          address: { ...prev.address, [field]: field === 'countryCode' ? value.toUpperCase() : value },
        })),
      setAddressType: (addressType: AddressType) => update((prev) => ({ address: { ...prev.address, addressType } })),
      applyAddress: (address: DestinationAddress) => update(() => ({ address })),

      toggleCustomsOptIn: () => update((prev) => ({ customsOptIn: !prev.customsOptIn })),
      updateCustoms: (patch: Partial<CustomsInformation>) => update((prev) => ({ customs: { ...prev.customs, ...patch } })),
      updateCustomsItem: (key: string, patch: Partial<CustomsItemDraft>) =>
        update((prev) => ({ customsItems: patchByKey(prev.customsItems, key, () => patch) })),
      addCustomsItem: () => update((prev) => ({ customsItems: [...prev.customsItems, newCustomsItem()] })),
      removeCustomsItem: (key: string) =>
        update((prev) => (prev.customsItems.length > 1 ? { customsItems: withoutKey(prev.customsItems, key) } : {})),
    };
  }, []);

  return { form, actions };
}

export type LabelFormActions = ReturnType<typeof useLabelForm>['actions'];
